import { useState } from 'react';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import SideBarModal from '../../modal/SideBarModal';
import { LoadedUser, Section, SectionRow } from '../../../interfaces';
import { EmployeesResourceMap, ResourceBlock } from '../../../types/employees';
import useUser from '../../../hooks/global/useUser';
import {
  getDayNamesFromTimeBlock,
  getTimeBlockData,
} from '../../../helpers/employees';
import TimeBlockForm from '../../availability/TimeBlockForm';
import FullPageModal from '../../modal/FullPageModal';
import { invalidateBatchTimeBlocksEmployee } from '../../../services/employee';
import useDeleteTimeBlock from '../../../hooks/availability/useDeleteTimeBlock';
import useConfirm from '../../../hooks/useConfirm';
import useLoadingModal from '../../../hooks/global/useLoadingModal';

interface Props {
  timeBlock:
    | (ResourceBlock & {
        employeeId: string;
      })
    | null;
  employeesResourceMap?: EmployeesResourceMap;
  onClose: () => void;
  selectedArtists: string[];
}

function TimeBlockSideBarModal({
  timeBlock,
  employeesResourceMap,
  onClose,
  selectedArtists,
}: Props) {
  const confirm = useConfirm();
  const { openLoadingModal, closeLoadingModal } = useLoadingModal();
  const [isEditing, setIsEditing] = useState(false);
  const { user } = useUser<LoadedUser>();

  const { mutate: deleteTimeBlock } = useDeleteTimeBlock({
    onSuccess: () => {
      if (!timeBlock) return;
      toast.success('Time block deleted!');
      invalidateBatchTimeBlocksEmployee({
        employeeId: timeBlock.employeeId,
        selectedArtists,
      });
      onClose();
    },
    onMutate: () => {
      openLoadingModal({
        header: 'Deleting time block',
        message: 'Please wait while we delete the time block...',
      });
    },
    onSettled: closeLoadingModal,
  });

  if (!timeBlock) return null;

  const employee = user.business.employees.find(
    emp => emp.employeeId === timeBlock.employeeId,
  );
  if (!employee) return null;

  const { startDateString, endDateString, isAllDay, start, end } =
    getTimeBlockData({
      employee,
      employeesResourceMap,
      timeBlock,
    });

  const sections: Section[] = [
    ['ADMIN', 'SHOP_MANAGER'].includes(user.role)
      ? {
          name: 'Employee',
          rows: [
            {
              type: 'TEXT',
              label: 'Name',
              value: [
                `${employee.user?.firstName || ''} ${
                  employee.user?.lastName || ''
                }`.trim() || employee.displayName,
              ],
            },
          ],
        }
      : null,
    {
      name: 'Time Block Details',
      rows: [
        {
          type: 'TEXT',
          label: 'Name',
          value: [timeBlock.reason],
        },
        {
          type: 'TEXT',
          label: 'Repeats',
          value: [
            !timeBlock.repeat
              ? 'Does not repeat'
              : `Repeats ${
                  timeBlock.repeat?.frequency === 'W' ? 'weekly' : 'daily'
                }`,
          ],
        },
        timeBlock.repeat
          ? {
              type: 'TEXT',
              label: 'Repeats every',
              value: [
                `Every ${
                  timeBlock.repeat.interval > 1 ? timeBlock.repeat.interval : ''
                } ${
                  timeBlock.repeat.frequency === 'W'
                    ? `week${timeBlock.repeat.interval > 1 ? 's' : ''}`
                    : `day${timeBlock.repeat.interval > 1 ? 's' : ''}`
                }`,
              ],
            }
          : null,
        {
          type: 'TEXT',
          label: timeBlock.repeat ? 'Block Starting' : 'Blocked Day',
          value: [startDateString],
        },
        timeBlock.repeat
          ? {
              type: 'TEXT',
              label: 'Block Ending',
              value: [endDateString],
            }
          : null,
        isAllDay
          ? {
              type: 'TEXT',
              label: 'All Day',
              value: ['Yes'],
            }
          : {
              type: 'TEXT',
              label: 'From',
              value:
                start && end
                  ? [`${format(start, 'h:mm a')} - ${format(end, 'h:mm a')}`]
                  : [null, null],
            },
        timeBlock.repeats && timeBlock.repeat?.frequency === 'W'
          ? {
              type: 'TEXT',
              label: 'On',
              value: [getDayNamesFromTimeBlock(timeBlock).join(', ')],
            }
          : null,
      ].filter(Boolean) as SectionRow[],
    },
  ].filter(Boolean) as Section[];

  const onDelete = async () => {
    const confirmed = await confirm({
      title: 'Delete Time Block',
      message:
        'Are you sure you want to delete this time block? This action cannot be undone.',
      positiveText: 'Delete',
      negativeText: 'Cancel',
    });
    if (!confirmed) return;

    deleteTimeBlock({
      resourceBlockId: timeBlock.id!,
      employeeId: timeBlock.employeeId,
    });
  };

  return (
    <>
      <SideBarModal
        modalOpen={!!timeBlock}
        name="Time Block"
        sections={sections}
        setModalOpen={onClose}
        footerActions={[
          {
            action: () => setIsEditing(true),
            label: 'Edit',
          },
          {
            action: onDelete,
            label: 'Delete',
          },
        ]}
      />
      {isEditing && (
        <FullPageModal
          modalOpen={isEditing}
          setModalOpen={setIsEditing}
          name="Edit Time Block">
          <TimeBlockForm
            onClose={() => setIsEditing(false)}
            employeesResourceMap={employeesResourceMap}
            selectedArtists={selectedArtists}
            existing={{
              employee,
              resourceBlock: timeBlock,
            }}
          />
        </FullPageModal>
      )}
    </>
  );
}

export default TimeBlockSideBarModal;
